import { Component, Inject } from '@angular/core'
import {
    Action,
    ConfirmationService,
    ErrorsT,
    FieldTracker,
    FluentTracker,
    im,
    isPositiveInt,
    libValuesToMap,
    selfValuesFactory,
    SmartColumns,
    stringToMap,
    Table,
    TableControlType,
    TableDelegate,
    TableExportType,
    Via
} from '@surgetech-eu/core'
import moment from 'moment'
import uuid from 'uuid'
import { forkJoin, Observable, of, Subscriber } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { disabledColumnOptions, maxRank } from '../constant'
import { LibraryModel } from '../models'
import { CountryService } from '../services/country.service'
import { CountryOfOrigin, RegionOfSourcing, RegionOfSourcingService } from '../services/region-of-sourcing.service'

@Component({
    selector: 'app-region-of-sourcing',
    templateUrl: './region-of-sourcing.component.html',
    standalone: false
})
@SmartColumns()
export class RegionOfSourcingComponent implements TableDelegate {
    private table: Table
    private countriesByKey = new Map<string, LibraryModel>()
    private countryOptions = new Map<string, string>()
    private pendingSource: RegionOfSourcing[] = []
    private fluentTracker: FluentTracker

    saving = false
    saved = false
    error: ErrorsT = null

    importUrl = '/libraries/management/types/regionOfSourcing/import-entries'

    constructor(
        @Inject('surge.core.schema-transformers') private schemaTransformers: any[],
        private regionOfSourcingService: RegionOfSourcingService,
        private confirmationService: ConfirmationService,
        private countryService: CountryService
    ) {
        this.fluentTracker = new FluentTracker(new FieldTracker(), {
            fields: {
                id: {
                    ns: true
                },
                key: {
                    required: true,
                    validateIfEmpty: true,
                    validate: (value, extra, withinNs) => {
                        const key = typeof value === 'string' ? value.trim() : ''
                        if (!key) {
                            return "It's required"
                        }

                        const duplicate = this.pendingSource.some(
                            item =>
                                item.key?.trim()?.toLowerCase() === key.toLowerCase() &&
                                item.id?.trim()?.toLowerCase() !== withinNs?.trim()?.toLowerCase()
                        )
                        return duplicate ? 'Should have a unique value' : null
                    }
                },
                description: {
                    required: false
                },
                countryOfOrigin: {
                    required: true,
                    validateIfEmpty: true,
                    validate: (country: CountryOfOrigin | null | undefined) => {
                        const countryId = country?.id
                        if (!countryId) {
                            return "It's required"
                        }

                        return this.countriesByKey.has(countryId.toLowerCase())
                            ? null
                            : im('{value} is not found', { value: countryId })
                    }
                },
                rank: {
                    required: false,
                    validate: value => {
                        if (value === null || value === undefined || value === '') {
                            return null
                        }
                        if (!isPositiveInt(value)) {
                            return 'Should be a positive number'
                        }
                        if (value > maxRank) {
                            return im('It must be less than {maxRank}', { maxRank })
                        }
                        return null
                    }
                },
                disabled: {
                    required: false
                },
                new: {
                    required: false
                },
                library: {
                    required: true
                },
                namespace: {
                    required: true
                }
            }
        })
        this.fluentTracker.listen(FluentTracker.EVENT_CHANGE, () => (this.error = null), true)
    }

    getTableData(): Observable<RegionOfSourcing[]> {
        if (this.pendingSource.length) {
            return of(this.pendingSource)
        }

        return forkJoin([this.countryService.getAll(), this.regionOfSourcingService.getAll()]).pipe(
            map(([countries, regionsOfSourcing]) => {
                this.countriesByKey.clear()
                this.countryOptions.clear()
                countries.forEach(country => {
                    if (!country.key) {
                        return
                    }

                    const countryKey = country.key.toLowerCase()
                    this.countriesByKey.set(countryKey, country)
                    this.countryOptions.set(
                        countryKey,
                        country.description ? country.key + ' - ' + country.description : country.key
                    )
                })

                return regionsOfSourcing
            }),
            tap(regionsOfSourcing => (this.pendingSource = [...regionsOfSourcing]))
        )
    }

    onTableDelegateBind(table: Table): void {
        this.table = table
    }

    disableSupportQuery(): boolean {
        return true
    }

    getColumnsConfig() {
        return [
            {
                name: 'key',
                title: 'Region of Sourcing Key',
                type: 'text'
            },
            {
                name: 'description',
                title: 'Region of Sourcing Description',
                type: 'text'
            },
            {
                name: 'countryOfOrigin',
                title: 'Country of Origin',
                type: 'text'
            },
            {
                name: 'createdAt',
                title: 'Entry Date',
                type: 'date'
            },
            {
                name: 'createdBy',
                title: 'User',
                type: 'text'
            },
            {
                name: 'updatedAt',
                title: 'Date of Last Update',
                type: 'date'
            },
            {
                name: 'updatedBy',
                title: 'User Last Update',
                type: 'text'
            },
            {
                name: 'rank',
                title: 'Rank'
            },
            {
                name: 'disabled',
                title: 'Disabled',
                type: 'boolean'
            }
        ]
    }

    customizeColumns(columnsMap) {
        const keyColumn = columnsMap.get('key')
        keyColumn.control = {
            type: TableControlType.INPUT,
            changing: (value, row) => this.updateFluentValue(value, row, 'key'),
            canShow: (value, row) => row.new,
            failed: (value, row) => this.fluentTracker.tracker.hasError(row.id, 'key'),
            failure: (value, row) => this.fluentTracker.tracker.getError(row.id, 'key'),
            seed: row => this.getTrackedValue(row, 'key')
        }
        keyColumn.filter = {
            via: Via.TEXT
        }

        const descriptionColumn = columnsMap.get('description')
        descriptionColumn.control = {
            type: TableControlType.INPUT,
            changing: (value, row) => this.updateFluentValue(value, row, 'description'),
            canShow: () => true,
            failed: (value, row) => this.fluentTracker.tracker.hasError(row.id, 'description'),
            failure: (value, row) => this.fluentTracker.tracker.getError(row.id, 'description'),
            seed: row => this.getTrackedValue(row, 'description')
        }
        descriptionColumn.filter = {
            via: Via.TEXT
        }

        const countryOfOriginColumn = columnsMap.get('countryOfOrigin')
        countryOfOriginColumn.control = {
            type: TableControlType.SELECT,
            values: () => this.countryOptions,
            change: (countryKey: string, row) => {
                const country = this.countriesByKey.get(countryKey)
                this.updateFluentValue(
                    country?.key
                        ? {
                              id: country.key,
                              code: country.description
                          }
                        : null,
                    row,
                    'countryOfOrigin'
                )
            },
            canShow: () => true,
            failed: (value, row) => this.fluentTracker.tracker.hasError(row.id, 'countryOfOrigin'),
            failure: (value, row) => this.fluentTracker.tracker.getError(row.id, 'countryOfOrigin'),
            seed: row => this.getTrackedValue(row, 'countryOfOrigin')?.id?.toLowerCase()
        }
        countryOfOriginColumn.pipe = (country: CountryOfOrigin | null | undefined) => this.getCountryLabel(country)
        countryOfOriginColumn.filter = {
            via: Via.SELF,
            selfValues: selfValuesFactory('countryOfOrigin', libValuesToMap),
            path: 'countryOfOrigin.id'
        }

        const createdByColumn = columnsMap.get('createdBy')
        createdByColumn.filter = {
            via: Via.SELF,
            selfValues: selfValuesFactory('createdBy', stringToMap),
            path: 'createdBy'
        }

        const rankColumn = columnsMap.get('rank')
        rankColumn.control = {
            type: TableControlType.INPUT,
            changing: (value, row) => this.updateFluentValue(value, row, 'rank'),
            canShow: () => true,
            failed: (value, row) => this.fluentTracker.tracker.hasError(row.id, 'rank'),
            failure: (value, row) => this.fluentTracker.tracker.getError(row.id, 'rank'),
            seed: row => this.getTrackedValue(row, 'rank')
        }
        rankColumn.filter = {
            via: Via.TEXT
        }

        const disabledColumn = columnsMap.get('disabled')
        disabledColumn.control = {
            type: TableControlType.DROPDOWN,
            values: disabledColumnOptions,
            required: true,
            change: (value, row) => this.updateFluentValue(value, row, 'disabled'),
            canShow: () => true,
            seed: row => String(this.getTrackedValue(row, 'disabled') ?? false)
        }
    }

    canTableRemove(): boolean {
        return true
    }

    configureTableRemoveAction(action: Action): void {
        action.disabled = (row: RegionOfSourcing) => row.isUsed
        action.run = (row: RegionOfSourcing) => {
            return new Observable(observer => {
                const confirmation = this.confirmationService.openWithParams({
                    text: 'Are you sure that you want to delete this entry?',
                    title: 'Delete'
                })
                confirmation
                    .then(() => {
                        this.error = null
                        this.saved = false

                        if (row.new) {
                            this.removeItem(row.id)
                            observer.next(true)
                            return
                        }

                        const rowToRemove = { ...row }
                        delete rowToRemove.__index
                        delete rowToRemove.__internalRowNumbers
                        delete rowToRemove.__sortIndex
                        this.regionOfSourcingService.remove([rowToRemove]).subscribe(
                            data => {
                                this.removeItem(row.id)
                                observer.next(data)
                            },
                            error => {
                                this.error = error
                            }
                        )
                    })
                    .catch(() => {
                        observer.next(true)
                    })
            })
        }
    }

    hasTableErrorForRow(row): boolean {
        return this.fluentTracker.hasErrors(row.id)
    }

    canSave(): boolean {
        return this.fluentTracker.hasValues() && !this.fluentTracker.hasErrors() && !this.saving
    }

    canTableExport(): boolean {
        return true
    }

    getTableExportTypes(): TableExportType[] {
        return [
            {
                name: 'xlsx',
                url: '/libraries/management/types/regionOfSourcing/export-entries/xlsx'
            }
        ]
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mapBeforeTableExportDirectly(rows: any[]) {
        this.fluentTracker.validateAll()
        const canExportAllRows = !this.fluentTracker.hasErrors()

        return rows
            .map(item => {
                const copy = JSON.parse(JSON.stringify(item))
                const isNew = Boolean(copy.new)
                delete copy.__index
                delete copy.__internalRowNumbers
                delete copy.__sortIndex
                delete copy.new
                return {
                    item: this.convertItem(copy),
                    isNew
                }
            })
            .filter(({ isNew }) => canExportAllRows || !isNew)
            .map(({ item }) => item)
    }

    generateTableExportFilename(type: TableExportType): string {
        return 'BC-' + moment().format('YYYY-MM-DD') + '-Handbook-RegionOfSourcing.xlsx'
    }

    shouldTableExportDirectly(): boolean {
        return true
    }

    get importTemplateUrl(): string {
        return '/libraries/management/types/regionOfSourcing/import-entries/template.xlsx'
    }

    importFailed(error): void {
        this.error = typeof error === 'string' ? error : 'Unable to import data.'
    }

    beforeImport(): void {
        this.error = null
        this.saved = false
    }

    afterImport(imported: Partial<RegionOfSourcing>[]): void {
        if (!imported?.length) {
            return
        }

        const existingKeys = new Set<string>()
        this.pendingSource.forEach(item => {
            const key = item.key?.trim().toLowerCase() ?? ''
            if (key) {
                existingKeys.add(key)
            }
        })

        const importedIds: string[] = []
        let hasDuplicates = false
        let hasInvalidRank = false

        imported.forEach(entry => {
            const newItem = this.createImportedItem(entry)
            const key = newItem.key?.trim().toLowerCase() ?? ''
            if (key) {
                hasDuplicates = hasDuplicates || existingKeys.has(key)
                existingKeys.add(key)
            }

            const rank = newItem.rank as number | string | null | undefined
            const hasRankValue = rank !== null && rank !== undefined && rank !== ''
            if (hasRankValue && (!isPositiveInt(rank) || Number(rank) > maxRank)) {
                hasInvalidRank = true
            }

            this.pendingSource.push(newItem)
            this.updateExistingFields(newItem)
            importedIds.push(newItem.id)
        })

        importedIds.forEach(id => this.fluentTracker.validateAllFields(id))

        const errors: ErrorsT = []
        if (hasDuplicates) {
            errors.push('The key must be unique.')
        }
        if (hasInvalidRank) {
            errors.push(im('Rank should be a positive number and less than {maxRank}.', { maxRank }))
        }
        if (this.fluentTracker.hasErrors()) {
            if (!errors.length) {
                errors.push('Some of the imported entries have errors. Please check the data.')
            }
            this.error = errors
        }

        this.table.reload()
        this.table.goToLastPage()
    }

    addNewItem(): void {
        const newItem = this.createNewItem()
        this.pendingSource.push(newItem)
        this.updateExistingFields(newItem)
        this.saved = false
        this.error = null
        this.table.reload()
        this.table.goToLastPage()
    }

    save(): void {
        const data = this.convertPayload()
        if (!data) {
            return
        }

        const requests: Observable<RegionOfSourcing[]>[] = []
        if (data.create.length) {
            requests.push(this.regionOfSourcingService.create(data.create))
        }
        if (data.update.length) {
            requests.push(this.regionOfSourcingService.update(data.update))
        }
        if (!requests.length) {
            return
        }

        this.error = null
        this.saved = false
        this.saving = true
        forkJoin(requests).subscribe(
            () => {
                this.pendingSource = []
                this.fluentTracker.clearAll()
                this.table.reload(
                    new Subscriber(() => {
                        this.saving = false
                        this.saved = true
                    })
                )
            },
            error => this.handleSaveError(error)
        )
    }

    private createNewItem(): RegionOfSourcing {
        return {
            id: uuid(),
            key: '',
            description: '',
            countryOfOrigin: null,
            rank: null,
            disabled: false,
            new: true,
            library: 'regionOfSourcing',
            namespace: 'kering'
        }
    }

    private createImportedItem(entry: Partial<RegionOfSourcing>): RegionOfSourcing {
        const defaults = this.createNewItem()
        return this.convertItem({
            ...defaults,
            ...entry,
            id: defaults.id,
            disabled: false,
            new: true,
            library: 'regionOfSourcing',
            namespace: 'kering'
        })
    }

    private convertPayload(): { create: RegionOfSourcing[]; update: RegionOfSourcing[] } | null {
        this.fluentTracker.validateAll()
        if (this.fluentTracker.hasErrors()) {
            this.table.refresh()
            return null
        }

        const tracker = this.fluentTracker.generate()
        const data = {
            create: [],
            update: []
        }
        tracker.forEach(item => {
            const payload = this.convertItem(item)
            const isNew = payload.new
            delete payload.new
            if (isNew) {
                data.create.push(payload)
                return
            }
            data.update.push(payload)
        })
        return data
    }

    private removeItem(id: string): void {
        this.fluentTracker.tracker.destroy(id)
        const removedIndex = this.pendingSource.findIndex(item => item.id === id)
        this.pendingSource.splice(removedIndex, 1)

        this.table.reload()
    }

    private convertItem(item: RegionOfSourcing): RegionOfSourcing {
        return {
            ...item,
            disabled: String(item.disabled) === 'true',
            countryOfOrigin: this.normalizeCountryOfOrigin(item.countryOfOrigin)
        }
    }

    private normalizeCountryOfOrigin(country: CountryOfOrigin | null | undefined): CountryOfOrigin | null {
        if (!country) {
            return null
        }

        const countryId = country.id.trim()
        if (!countryId) {
            return null
        }

        const found = this.countriesByKey.get(countryId.toLowerCase())
        return found?.key
            ? {
                  id: found.key,
                  code: found.description
              }
            : {
                  ...country,
                  id: countryId
              }
    }

    private updateFluentValue(value, row, column: string): void {
        const index = this.pendingSource.findIndex(item => item.id === row.id)
        if (index !== -1) {
            Object.assign(this.pendingSource[index], { [column]: value })
            Object.assign(row, { [column]: value })
        }
        this.updateExistingFields(row)
        this.fluentTracker.update(row.id, column, value)
    }

    private updateExistingFields(row: RegionOfSourcing): void {
        if (row.key) {
            this.updateTrackerIfMissing(row, 'key', row.key)
        }
        this.updateTrackerIfMissing(row, 'description', row.description ?? '')
        if (row.countryOfOrigin) {
            this.updateTrackerIfMissing(row, 'countryOfOrigin', row.countryOfOrigin)
        }
        this.updateTrackerIfMissing(row, 'rank', row.rank ?? null)
        this.updateTrackerIfMissing(row, 'disabled', String(row.disabled ?? false))
        this.updateTrackerIfMissing(row, 'new', row.new ?? false)
        this.updateTrackerIfMissing(row, 'library', row.library)
        this.updateTrackerIfMissing(row, 'namespace', row.namespace)
    }

    private updateTrackerIfMissing(row: RegionOfSourcing, field: string, value): void {
        if (!this.fluentTracker.tracker.hasValue(row.id, field)) {
            this.fluentTracker.update(row.id, field, value)
        }
    }

    private getTrackedValue(row: RegionOfSourcing, field: string) {
        return this.fluentTracker.tracker.hasValue(row.id, field)
            ? this.fluentTracker.tracker.getValue(row.id, field)
            : row[field]
    }

    private getCountryLabel(country: CountryOfOrigin | null | undefined): string {
        if (!country) {
            return ''
        }

        return this.countryOptions.get(country.id.toLowerCase()) ?? country.id
    }

    private handleSaveError(error): void {
        this.saving = false
        if (error && typeof error === 'object') {
            this.error = Object.entries(error)
                .map(([key, value]) => key + ' ' + value)
                .join('; ')
            return
        }
        this.error = error || 'Unable to save data.'
    }
}
